import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FaBuilding, FaCode, FaMicrosoft, FaPlus, FaTools, FaUsers } from "react-icons/fa";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { Layout } from "../../../../common/layout/Layout";
import {
  UserManagementTab,
  useQueryParams,
} from "../../../../common/useQueryParams";
import { departmentsAreEnabled } from "../../../../common/departments/departmentVisibility";
import { CONTENT_WIDTH } from "../../../../config";
import { Advanced } from "./Advanced";
import { Departments } from "./Departments";
import { administersSomeDepartment } from "./roleAdministration";
import { ApiAccess } from "./ApiAccess";
import { AzureConfiguration } from "./AzureConfiguration";
import { Invites } from "./Invites";
import { Users } from "./Users";
export interface AddAdminFormValues {
  email: string;
}

interface TabDefinition {
  key: UserManagementTab;
  label: string;
  icon: IconType;
  iconBoxSize?: string;
  panel: ReactNode;
}

export const AccessManagementPage = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  const authState = useLoggedInWithOrgContextState();
  const params = useQueryParams();
  const { t } = useTranslation();

  const managesOrganization = authState.selectedOrg.canManageUsers;
  const showsDepartments =
    departmentsAreEnabled() && (managesOrganization || administersSomeDepartment(authState.selectedOrg));

  const tabs: TabDefinition[] = [
    ...(managesOrganization
      ? [
          { key: "users" as const, label: t("Users"), icon: FaUsers, panel: <Users /> },
          {
            key: "invites" as const,
            label: t("Add User"),
            icon: FaPlus,
            iconBoxSize: "3",
            panel: <Invites org={authState.selectedOrg} />,
          },
        ]
      : []),
    ...(showsDepartments
      ? [
          {
            key: "departments" as const,
            label: t("Departments"),
            icon: FaBuilding,
            panel: <Departments />,
          },
        ]
      : []),
    ...(managesOrganization
      ? [
          { key: "azure" as const, label: t("Entra ID"), icon: FaMicrosoft, panel: <AzureConfiguration /> },
          { key: "advanced" as const, label: t("Advanced"), icon: FaTools, panel: <Advanced /> },
          { key: "api" as const, label: t("API"), icon: FaCode, panel: <ApiAccess /> },
        ]
      : []),
  ];

  const tabIndex = Math.max(
    0,
    tabs.findIndex((candidate) => candidate.key === params.get("tab")),
  );

  const tabListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tabListRef.current?.children[tabIndex]?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [tabIndex]);

  const content = (
    <Flex flexDirection="column" width="full" maxWidth="full" height="full">
      <Flex
        alignItems="center"
        borderBottom={"1px solid"}
        borderColor="smBorder"
        pt="4"
        pb="5"
      >
        <Heading as="h2" size="lg">
          {t("Access management")}
        </Heading>
      </Flex>
      <Box maxWidth="full">
        <Tabs
          maxWidth="full"
          mt="5"
          index={tabIndex}
          onChange={(index) => params.setSelectedUserManagementTab(tabs[index].key)}
          variant="line"
          colorScheme="teal"
        >
          <TabList ref={tabListRef} overflowX="auto" overflowY="hidden">
            {tabs.map((tab) => (
              <Tab key={tab.key} flexShrink="0" whiteSpace="nowrap">
                <Icon mr="2" as={tab.icon} boxSize={tab.iconBoxSize} /> {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels maxWidth="full">
            {tabs.map((tab) => (
              <TabPanel key={tab.key} maxWidth="full">
                {tab.panel}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  );

  if (embedded) {
    return (
      <Box maxW="container.xl" mx="auto">
        {content}
      </Box>
    );
  }

  return (
    <Layout>
      <Grid
        maxWidth="full"
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"1fr"}
        templateAreas={`
            "leftContent content rightContent"
            `}
      >
        <GridItem
          maxWidth="full"
          area={"content"}
          px={{ sm: "2", base: "2.5" }}
        >
          {content}
        </GridItem>
      </Grid>
    </Layout>
  );
};
